import React from 'react'
import { Box, useTheme } from '@mui/material'
import AlarmOutlined from '@mui/icons-material/AlarmOutlined'

import Txt from './Txt'
import IconButton from './IconButton'
import TextField, { TextFieldRef } from './TextField'
import {
	LETTER_PREVIEW_URL_QUERY,
	RestaurantCommunication,
	useRestaurantCommunications,
} from '../utils/settlement'
import client from '../utils/client'
import addRestaurantCommunication from '../mutations/addRestaurantCommunication'
import updateRestaurantCommunication from '../mutations/updateRestaurantCommunication'
import deleteRestaurantCommunication from '../mutations/deleteRestaurantCommunication'
import { handleMutationError } from '../utils/errors'

const CHANNEL_LABELS: Record<RestaurantCommunication['channel'], string> = {
	letter: 'Brief',
	postcard: 'Postkarte',
	email: 'Mail',
	call: 'Anruf',
	note: 'Notiz',
	alarm: 'Alarm',
}

const sxDivider = { borderTop: '1px solid', borderColor: 'divider', m: '0.75rem 0' } as const

const sxEntry = { p: '0.3rem 0', borderBottom: '1px solid', borderColor: 'divider' } as const

const sxEntryHead = { display: 'flex', gap: '0.5rem', alignItems: 'baseline' } as const

const sxAlarmWhen = { display: 'flex', gap: '0.5rem', alignItems: 'center', m: '0 0 0.25rem 0' } as const

const sxHeader = { display: 'flex', gap: '0.5rem', alignItems: 'center', m: '0 0 0.25rem 0' } as const

export const formatDateTime = (dateTime: string): string =>
	new Date(dateTime).toLocaleString('de-DE', {
		day: 'numeric',
		month: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})

const sxAlarm = {
	display: 'flex',
	gap: '0.5rem',
	alignItems: 'center',
	p: '0.3rem 0.5rem',
	m: '0 0 0.25rem 0',
	border: '1px solid',
	borderColor: 'warning.main',
	borderRadius: '0.25rem',
} as const

const sxAlarmIcon = { fontSize: '1.1rem', color: 'warning.main', flexShrink: 0 } as const

// Zeigt zwei Zeilen, ein Klick klappt den ganzen Text auf und wieder zu.
const ExpandableNote = React.memo(
	({ text, color }: { text: string | null; color?: string }): JSX.Element => {
		const [expanded, setExpanded] = React.useState(false)
		const toggleExpanded = React.useCallback(() => setExpanded((wasExpanded) => !wasExpanded), [])

		return (
			<Txt
				variant="body2"
				color={color}
				flex="1"
				whiteSpace="pre-line"
				numberOfLines={expanded ? undefined : 2}
				onClick={toggleExpanded}
				noUnderLine
			>
				{text}
			</Txt>
		)
	}
)
ExpandableNote.displayName = 'ExpandableNote'

// Enter speichert den geänderten Text, Umschalt+Enter bricht die Zeile um.
const NoteEditor = React.memo(
	({ id, note, onSaved }: { id: string; note: string; onSaved: () => void }): JSX.Element => {
		const [editedNote, setEditedNote] = React.useState(note)
		const handleSave = React.useCallback(async () => {
			const result = await updateRestaurantCommunication({ id, note: editedNote })
			if (result.success) {
				onSaved()
			} else {
				handleMutationError('updateRestaurantCommunication', result.errors)
			}
		}, [id, editedNote, onSaved])

		return (
			<TextField
				size="small"
				multiline
				maxRows={6}
				defaultValue={note}
				onChange={setEditedNote}
				onEnter={handleSave}
				autoFocus
				flex
				m="0.25rem 0"
			/>
		)
	}
)
NoteEditor.displayName = 'NoteEditor'

// Ein offener Alarm, vorn sein Termin; die Mail geht eine Stunde vorher raus, dann verschwindet er.
const AlarmEntry = React.memo(
	({ alarm }: { alarm: RestaurantCommunication }): JSX.Element => {
		const theme = useTheme()
		const [editing, setEditing] = React.useState(false)
		const toggleEditing = React.useCallback(() => setEditing((wasEditing) => !wasEditing), [])
		const handleDelete = React.useCallback(async () => {
			const result = await deleteRestaurantCommunication({ id: alarm.id })
			if (!result.success) handleMutationError('deleteRestaurantCommunication', result.errors)
		}, [alarm.id])

		return (
			<Box sx={sxAlarm}>
				<AlarmOutlined sx={sxAlarmIcon} />
				<Txt variant="body2" fontWeight="bold" color="warning.main" noWrap>
					{formatDateTime(alarm.dueAt as string)}
				</Txt>
				{editing ? (
					<NoteEditor id={alarm.id} note={alarm.note ?? ''} onSaved={toggleEditing} />
				) : (
					<ExpandableNote text={alarm.note} color="warning.main" />
				)}
				<IconButton
					iconName="EditRounded"
					iconColor={theme.palette.warning.main}
					toolTip={editing ? 'Bearbeiten abbrechen' : 'Alarm bearbeiten'}
					onClick={toggleEditing}
				/>
				<IconButton
					iconName="DeleteOutline"
					iconColor={theme.palette.warning.main}
					toolTip="Alarm löschen"
					onClick={handleDelete}
				/>
			</Box>
		)
	}
)
AlarmEntry.displayName = 'AlarmEntry'

// Anrufe, Notizen und Briefe lassen sich löschen, etwa ein Brief, der nie rausging; Karten und Mails nicht.
// Anrufe und Notizen lassen sich außerdem bearbeiten.
const CommunicationEntry = React.memo(
	({ communication }: { communication: RestaurantCommunication }): JSX.Element => {
		const [editing, setEditing] = React.useState(false)
		const toggleEditing = React.useCallback(() => setEditing((wasEditing) => !wasEditing), [])
		const handleDelete = React.useCallback(async () => {
			const result = await deleteRestaurantCommunication({ id: communication.id })
			if (!result.success) handleMutationError('deleteRestaurantCommunication', result.errors)
		}, [communication.id])

		// Das Fenster sofort öffnen, sonst blockt der Browser es nach der Abfrage als Popup.
		const handleOpenLetter = React.useCallback(async () => {
			const letterWindow = window.open('', '_blank')
			const { data } = await client.query({
				query: LETTER_PREVIEW_URL_QUERY,
				variables: { printJobId: communication.reference },
			})
			if (letterWindow) letterWindow.location.href = data.letterPreviewUrl
		}, [communication.reference])

		return (
			<Box sx={sxEntry}>
				<Box sx={sxEntryHead}>
					<Txt variant="body2" fontWeight="bold">
						{CHANNEL_LABELS[communication.channel]}
					</Txt>
					<Txt variant="caption" color="text.secondary" flex="1" noWrap>
						{communication.settlementNames.join(', ')}
					</Txt>
					<Txt variant="caption" color="text.secondary">
						{formatDateTime(communication.createdAt)}
					</Txt>
					{communication.channel === 'letter' && communication.reference ? (
						<IconButton
							iconName="OpenInNew"
							iconColor="text.secondary"
							toolTip="Brief bei LetterXpress öffnen"
							onClick={handleOpenLetter}
						/>
					) : null}
					{communication.channel === 'call' || communication.channel === 'note' ? (
						<IconButton
							iconName="EditRounded"
							iconColor="text.secondary"
							toolTip={editing ? 'Bearbeiten abbrechen' : 'Bearbeiten'}
							onClick={toggleEditing}
						/>
					) : null}
					{communication.channel === 'call' ||
					communication.channel === 'note' ||
					communication.channel === 'letter' ? (
						<IconButton
							iconName="DeleteOutline"
							iconColor="text.secondary"
							toolTip="Löschen"
							onClick={handleDelete}
						/>
					) : null}
				</Box>
				{editing ? (
					<NoteEditor
						id={communication.id}
						note={communication.note ?? ''}
						onSaved={toggleEditing}
					/>
				) : communication.subject || communication.note ? (
					<ExpandableNote text={communication.subject ?? communication.note} />
				) : null}
				{communication.reference && communication.channel !== 'letter' ? (
					<Txt variant="caption" color="text.secondary">
						{communication.channel === 'email'
							? `an ${communication.reference}`
							: `Auftrag ${communication.reference}`}
					</Txt>
				) : null}
			</Box>
		)
	}
)
CommunicationEntry.displayName = 'CommunicationEntry'

// Offene Alarme und alle Kontakte mit dem Restaurant; Anrufe, Notizen und Alarme werden hier
// eingetragen, Briefe, Karten und Mails trägt der Versand selbst ein.
const RestaurantCommunicationLog = React.memo(
	({ slug }: { slug: string }): JSX.Element => {
		const theme = useTheme()
		const { communications } = useRestaurantCommunications(slug)
		// Offene Alarme klappt der Wecker neben der Überschrift auf, der nächste zuerst.
		const alarms = React.useMemo(
			() =>
				communications
					.filter(({ channel }) => channel === 'alarm')
					.sort((a, b) => (a.dueAt as string).localeCompare(b.dueAt as string)),
			[communications]
		)
		const contacts = React.useMemo(
			() => communications.filter(({ channel }) => channel !== 'alarm'),
			[communications]
		)
		const [showAlarms, setShowAlarms] = React.useState(false)
		const toggleAlarms = React.useCallback(() => setShowAlarms((shown) => !shown), [])
		const noteRef = React.useRef<TextFieldRef>(null)
		const [note, setNote] = React.useState('')
		// Enter blendet Datum und Uhrzeit ein; ein zweites Enter verwirft sie und trägt eine Notiz ein,
		// der Haken setzt stattdessen die Erinnerung.
		const [askAlarmWhen, setAskAlarmWhen] = React.useState(false)
		const [alarmDate, setAlarmDate] = React.useState('')
		const [alarmTime, setAlarmTime] = React.useState('')

		const handleSaveAlarm = React.useCallback(async () => {
			if (!note.trim() || !alarmDate || !alarmTime) return
			const result = await addRestaurantCommunication({
				slug,
				channel: 'alarm',
				note,
				dueAt: new Date(`${alarmDate}T${alarmTime}`).toISOString(),
			})
			if (result.success) {
				noteRef.current?.reset()
				setAskAlarmWhen(false)
			} else {
				handleMutationError('addRestaurantCommunication', result.errors)
			}
		}, [slug, note, alarmDate, alarmTime])

		// Das erste Enter öffnet den Termin, vorbelegt mit morgen um dieselbe Zeit; ein leeres Feld tut nichts.
		const handleEnter = React.useCallback(async () => {
			if (!note.trim()) return
			if (askAlarmWhen) {
				const result = await addRestaurantCommunication({ slug, channel: 'note', note })
				if (result.success) {
					noteRef.current?.reset()
					setAskAlarmWhen(false)
				} else {
					handleMutationError('addRestaurantCommunication', result.errors)
				}
			} else {
				const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
				setAlarmDate(tomorrow.toLocaleDateString('sv-SE'))
				setAlarmTime(tomorrow.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }))
				setAskAlarmWhen(true)
			}
		}, [slug, note, askAlarmWhen])

		return (
			<>
				<Box sx={sxDivider} />
				<Box sx={sxHeader}>
					<Txt variant="subtitle2">Kommunikation</Txt>
					{alarms.length ? (
						<IconButton
							iconName="AccessAlarm"
							iconColor={theme.palette.warning.main}
							toolTip={alarms.length === 1 ? '1 Alarm' : `${alarms.length} Alarme`}
							onClick={toggleAlarms}
						/>
					) : null}
				</Box>
				{showAlarms ? alarms.map((alarm) => <AlarmEntry key={alarm.id} alarm={alarm} />) : null}
				<TextField
					ref={noteRef}
					placeHolder={
						askAlarmWhen
							? 'Enter: als Notiz eintragen · Haken: Erinnerung setzen'
							: 'Notiz, mit Enter weiter'
					}
					size="small"
					multiline
					maxRows={6}
					onChange={setNote}
					onEnter={handleEnter}
					m="0 0 0.25rem 0"
				/>
				{askAlarmWhen ? (
					<Box sx={sxAlarmWhen}>
						<TextField
							type="date"
							size="small"
							defaultValue={alarmDate}
							onChange={setAlarmDate}
							flex
						/>
						<TextField
							type="time"
							size="small"
							defaultValue={alarmTime}
							onChange={setAlarmTime}
							flex
						/>
						<IconButton iconName="Check" toolTip="Erinnerung setzen" onClick={handleSaveAlarm} />
					</Box>
				) : null}
				{contacts.map((communication) => (
					<CommunicationEntry key={communication.id} communication={communication} />
				))}
			</>
		)
	}
)
RestaurantCommunicationLog.displayName = 'RestaurantCommunicationLog'

export default RestaurantCommunicationLog
